import { productTextPolicyFindingSchema } from "../product-text-policy"
import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { requirementLevelSchema } from "./marketplace"
import { attributeFormatSchema } from "./attribute-format"
import {
  listingRunSchema,
  runItemVerdictReasonSchema,
  runItemVerdictSchema,
  runOutputWarningSchema,
} from "./runs"
import { runSkuRecordSchema } from "./tracked-skus"

// ── Run product previews ──────────────────────────────────────────────────

export const attributeRequirementStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  filled: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
  missingCodes: z.array(z.string()),
})
export type AttributeRequirementStats = z.infer<typeof attributeRequirementStatsSchema>

export const runProductPreviewErrorKindSchema = z.enum([
  "magento_missing",
  "magento_error",
  "missing_category",
  "schema_missing",
  "attributes_missing",
])
export type RunProductPreviewErrorKind = z.infer<typeof runProductPreviewErrorKindSchema>

export const runProductPreviewErrorSchema = z.object({
  kind: runProductPreviewErrorKindSchema,
  message: z.string(),
})
export type RunProductPreviewError = z.infer<typeof runProductPreviewErrorSchema>

export const runProductPreviewVariantSchema = z.object({
  sku: z.string(),
  name: z.string(),
  ean: z.string(),
  size: z.string(),
  color: z.string(),
  image: z.string(),
  stockStatus: z.enum(["IN_STOCK", "OUT_OF_STOCK", ""]),
})
export type RunProductPreviewVariant = z.infer<typeof runProductPreviewVariantSchema>

export const runProductPreviewMagentoSchema = z.object({
  sku: z.string(),
  type: z.enum(["configurable", "simple"]),
  nameStore: z.string(),
  nameEn: z.string(),
  manufacturer: z.string(),
  breadcrumb: z.string(),
  categoryLeaf: z.string(),
  mainImage: z.string(),
  price: z.number().nullable(),
  finalPrice: z.number().nullable(),
  currency: z.string(),
  stockStatus: z.enum(["IN_STOCK", "OUT_OF_STOCK"]),
  enabled: z.boolean(),
  localBan: z.boolean(),
  longTermUnavailable: z.boolean(),
  isDelisted: z.boolean(),
  variants: z.array(runProductPreviewVariantSchema),
})
export type RunProductPreviewMagento = z.infer<typeof runProductPreviewMagentoSchema>

export const runGeneratedAttributesPreviewSchema = z.object({
  config: z.record(z.string(), z.string()),
  simples: z.array(z.object({ sku: z.string(), attributes: z.record(z.string(), z.string()) })),
  writtenAt: z.string().nullable(),
})
export type RunGeneratedAttributesPreview = z.infer<typeof runGeneratedAttributesPreviewSchema>

export const runProductAttributeStatsSchema = z.object({
  required: attributeRequirementStatsSchema,
  recommended: attributeRequirementStatsSchema,
  optional: attributeRequirementStatsSchema,
})
export type RunProductAttributeStats = z.infer<typeof runProductAttributeStatsSchema>

export const runProductPreviewSchema = z.object({
  sku: runSkuRecordSchema,
  product: runProductPreviewMagentoSchema.nullable(),
  generated: runGeneratedAttributesPreviewSchema.nullable(),
  attributeStats: runProductAttributeStatsSchema.nullable(),
  errors: z.array(runProductPreviewErrorSchema),
})
export type RunProductPreview = z.infer<typeof runProductPreviewSchema>

export const runProductsResponseSchema = z.object({
  run: listingRunSchema,
  products: z.array(runProductPreviewSchema),
})
export type RunProductsResponse = z.infer<typeof runProductsResponseSchema>

export const runFeedPreviewColumnSchema = z.object({
  code: z.string(),
  label: z.string(),
  /** Human labels for the raw feed value codes present in this column, keyed by
   *  code (e.g. `{ "196": "Pakistan" }`). Only set for value-list/hierarchy
   *  attributes whose cells would otherwise render as opaque codes. */
  valueLabels: z.record(z.string(), z.string()).optional(),
})
export type RunFeedPreviewColumn = z.infer<typeof runFeedPreviewColumnSchema>

export const runFeedPreviewRowSchema = z.object({
  configSku: z.string(),
  simpleSku: z.string(),
  values: z.record(z.string(), z.string()),
  /** Attribute codes this row is missing that the category requires. Derived
   *  from the FULL missing-required gap set (not the display-capped `gaps`
   *  list), so every row carries its own complete signal regardless of where it
   *  falls in the page. */
  missingRequired: z.array(z.string()),
  /** True when this variant is eligible to list but has no offer on the
   *  marketplace yet, per the live offer snapshot. The "new-only" offer build
   *  emits a row only for these; an operator surfaces them with a "needs offer"
   *  pill. False when the snapshot wasn't pulled OR the variant already has an
   *  offer. */
  needsOffer: z.boolean(),
  offerSku: z.string().optional(),
  /** UI-only placeholder: never an emitted variant or offer. */
  isPlaceholder: z.boolean().optional(),
})
export type RunFeedPreviewRow = z.infer<typeof runFeedPreviewRowSchema>

/** Present only when the request paginated (passed `limit`). `configSkuCount`/
 *  `rowCount` then describe THIS page; `totalConfigSkus` counts candidates after
 *  search/category selection, before verdict filtering. Empty pages can have more.
 *  Absent ⇒ the response covers the entire run (legacy/full behaviour). */
export const runFeedPreviewPaginationSchema = z.object({
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  totalConfigSkus: z.number().int().nonnegative(),
  hasMore: z.boolean(),
})
export type RunFeedPreviewPagination = z.infer<typeof runFeedPreviewPaginationSchema>

/** A single build gap surfaced in the preview. The preview is non-blocking:
 *  it returns the best-effort rows AND the gaps that would stop a real feed
 *  build, so the operator sees what's filled and exactly what's left. `stage`
 *  says which gate the gap came from:
 *   - `agent`        — per-SKU synthetic gap (needs agent action, e.g. SKU not
 *                      found in Magento). `attrCode` is "*" for whole-SKU gaps.
 *   - `data_quality` — a build-stop the pipeline can't fix (e.g. a missing
 *                      nutrition table), or a row dropped by a data-quality gate.
 *   - `build`        — a CSV schema/value gap (missing required cell, an
 *                      operator code where a label is required, etc.).
 * `owner` is resolved by the API from the gap reason plus the marketplace's
 * canonical runtime config. Consumers schedule that owner directly instead of
 * re-reading marketplace files or guessing from vendor-specific attr codes. */
export const runGapOwnerSchema = z.enum([
  "extract",
  "copy",
  "nutrition",
  "image",
  "source",
  "writer",
])
export type RunGapOwner = z.infer<typeof runGapOwnerSchema>

export const runFeedPreviewGapSchema = z.object({
  stage: z.enum(["agent", "data_quality", "build"]),
  configSku: z.string(),
  simpleSku: z.string().nullable(),
  attrCode: z.string().nullable(),
  reason: z.string(),
  message: z.string(),
  owner: runGapOwnerSchema,
})
export type RunFeedPreviewGap = z.infer<typeof runFeedPreviewGapSchema>

/** A variant row the build silently removed from the feed (NOT a blocking gap,
 *  a parent becomes NOK only when no variants survive): a data-quality gate
 *  decided the row cannot list — e.g. a
 *  nutrition product with no rendered nutrition table (`nutrition_table_missing`)
 *  or a Decathlon size row with no resolvable SIZE_CPN (`size_cpn_unresolved`).
 *  Surfaced so a feed that's empty/shorter than its eligible-config count is
 *  never unexplained. `detail` carries the offending `attr=value` when known. */
export const runFeedPreviewDropSchema = z.object({
  configSku: z.string(),
  simpleSku: z.string(),
  reason: z.string(),
  detail: z.string().nullable(),
})
export type RunFeedPreviewDrop = z.infer<typeof runFeedPreviewDropSchema>

/** Current page classification. Saved validation remains separate provenance.
 * All members are included, even when they emit no feed rows. */
export const runFeedPreviewVerdictItemSchema = z.object({
  configSku: z.string(),
  verdict: runItemVerdictSchema.nullable(),
  reasons: z.array(runItemVerdictReasonSchema),
  name: z.string().nullable(),
  image: z.string().nullable(),
  /** Parked out of this run (`nova runs set-aside`), with the operator's reason.
   *  A parked config keeps its last verdict and still shows on the page, so
   *  without this a deliberately parked product is indistinguishable from a
   *  broken one — it renders as an OK card with no copy. */
  setAside: z.boolean(),
  setAsideReason: z.string().nullable(),
  evidence: z
    .object({
      kind: z.enum(["current", "historical", "unknown"]),
      savedVerdict: runItemVerdictSchema.nullable(),
      savedAt: z.string().nullable().optional(),
      savedReasons: z.array(runItemVerdictReasonSchema),
    })
    .optional(),
  /** Exact counts before the response's variant-evidence cap. */
  feedRowCount: z.number().int().nonnegative().optional(),
  droppedCount: z.number().int().nonnegative().optional(),
})
export type RunFeedPreviewVerdictItem = z.infer<typeof runFeedPreviewVerdictItemSchema>

export const runVariantFamilyConflictSchema = z.object({
  simpleSku: z.string(),
  configSkus: z.array(z.string()),
  occurrences: z.number().int().positive(),
})
export type RunVariantFamilyConflict = z.infer<typeof runVariantFamilyConflictSchema>

/** Page-scoped family audit derived from the exact transformed feed rows.
 * Conflict detail is bounded; counts stay exact. */
export const runVariantFamilyPlanSchema = z.discriminatedUnion("applicable", [
  z.object({ applicable: z.literal(false), reason: z.string() }),
  z.object({
    applicable: z.literal(true),
    attrCode: z.string(),
    source: z.literal("config_sku"),
    familyCount: z.number().int().nonnegative(),
    groupedRowCount: z.number().int().nonnegative(),
    singletonCount: z.number().int().nonnegative(),
    conflictCount: z.number().int().nonnegative(),
    conflictSample: z.array(runVariantFamilyConflictSchema),
    conflictMore: z.number().int().positive().optional(),
  }),
])
export type RunVariantFamilyPlan = z.infer<typeof runVariantFamilyPlanSchema>

/** Which feed COLUMN CODE carries each storefront role, so a UI can render a
 *  marketplace-storefront-style card (image + title) without hardcoding
 *  per-marketplace attribute codes. Each value indexes into `rows[].values`; a
 *  role is null when this marketplace's feed has no column for it. Stays
 *  marketplace-neutral: `title`/`brand` come from the marketplace config's copy
 *  + brand mapping, `image` from the primary image column emitted by the feed. */
export const runFeedPreviewRolesSchema = z.object({
  /** Generated product-title column (LLM copy) — the QA-suspect field. */
  title: z.string().nullable(),
  /** Primary product-image URL column. */
  image: z.string().nullable(),
  /** Brand column — used to strip the brand prefix off the title. */
  brand: z.string().nullable(),
  /** Marketplace-category column (the category attr code). Lets the grid show
   *  each config's marketplace category by reading `rows[].values[category]` and
   *  resolving the code through that column's `valueLabels` — no extra payload.
   *  Null when this page's feed has no category column. */
  category: z.string().nullable(),
})
export type RunFeedPreviewRoles = z.infer<typeof runFeedPreviewRolesSchema>

export const PREVIEW_FRESHNESS = {
  current: "current",
  historical: "historical",
  unknown: "unknown",
} as const
export const PREVIEW_RECOVERY = {
  replaying: "replaying",
  candidateBuilding: "candidate_building",
  awaitingActivation: "awaiting_activation",
  blocked: "blocked",
} as const

export const runFeedPreviewFreshnessSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal(PREVIEW_FRESHNESS.current) }),
  z.object({
    kind: z.literal(PREVIEW_FRESHNESS.unknown),
    reason: z.enum(["metadata_identity_unavailable", "inspection_inputs_changed"]),
    message: z.string(),
  }),
  z.object({
    kind: z.literal(PREVIEW_FRESHNESS.historical),
    generationId: z.number().int().positive(),
    marketplaceState: z.enum(["current", "catching_up", "stale"]),
    staleReason: z.string().nullable(),
    activationId: z.number().int().positive(),
    backlog: z.object({
      total: z.number().int().nonnegative(),
      counts: z.record(z.string(), z.number()),
    }),
    candidate: z
      .object({
        generationId: z.number().int().positive(),
        status: z.enum(["queued", "preparing", "building", "ready", "failed"]),
        inScopeProducts: z.number().int().nonnegative(),
        includedProducts: z.number().int().nonnegative(),
        excludedProducts: z.number().int().nonnegative(),
        technicalFailures: z.number().int().nonnegative(),
      })
      .nullable(),
    recovery: z.object({ kind: z.enum(PREVIEW_RECOVERY), message: z.string() }),
    unsafeProducts: z.object({
      items: z.array(
        z.object({ configSku: z.string(), reason: z.string(), readable: z.boolean() })
      ),
      total: z.number().int().nonnegative(),
      truncated: z.boolean(),
    }),
  }),
])
export type RunFeedPreviewFreshness = z.infer<typeof runFeedPreviewFreshnessSchema>

export const runFeedPreviewResponseSchema = z.object({
  policyFindings: z.array(productTextPolicyFindingSchema).optional(),
  freshness: runFeedPreviewFreshnessSchema,
  /** Shared generation/feed identity; mirror observations remain page-scoped. */
  inspectionIdentity: z.string().nullable().optional(),
  inspectedAt: z.string().optional(),
  run: listingRunSchema,
  format: z.enum(["csv", "json"]),
  columns: z.array(runFeedPreviewColumnSchema),
  rows: z.array(runFeedPreviewRowSchema),
  rowCount: z.number().int().nonnegative(),
  columnCount: z.number().int().nonnegative(),
  configSkuCount: z.number().int().nonnegative(),
  /** True when this page has at least one gap that would block a real feed
   *  build (`POST /api/feeds`). The preview itself never blocks — this just
   *  mirrors the build gate so the UI can show a "not yet buildable" notice.
   *  Scoped to the built page, like `rows`; whole-run readiness is at
   *  `GET /:slug/preconditions`. */
  buildBlocked: z.boolean(),
  /** Blocking gaps for the rendered page (capped at 200). `gapCount` is the
   *  true total before capping. Excludes non-blocking signals like
   *  `no_eligible_variants` (those configs are dropped from the feed, not
   *  blocked) — they surface on the SKU's verdict instead. */
  gaps: z.array(runFeedPreviewGapSchema),
  gapCount: z.number().int().nonnegative(),
  /** Gaps that do NOT block the build but still verdict their config NOK and
   *  count in `fixable` (capped at 200; `verdictOnlyGapCount` is the true
   *  total). They are deliberately absent from `gaps`, which mirrors the build
   *  gate — but `fixable` is what heal's convergence loop watches, so without
   *  this field a run could pin `fixable` above zero with nothing to name.
   *  Excludes `no_eligible_variants`: that one is a derived eligibility signal
   *  whose fixability is re-computed at verdict time
   *  (`derivedNoEligibleVariantsReason`), never heal's work. */
  verdictOnlyGaps: z.array(runFeedPreviewGapSchema),
  verdictOnlyGapCount: z.number().int().nonnegative(),
  /** Variant rows the build dropped from the feed on this page (capped at 200).
   *  `droppedCount` is the true total before capping. Unlike `gaps` these don't
   *  block the build — the build succeeds and just omits the row — but they DO
   *  explain why an eligible (OK-verdict) config can be absent from the feed. */
  dropped: z.array(runFeedPreviewDropSchema),
  droppedCount: z.number().int().nonnegative(),
  /** Run-level warnings that affect how an agent should interpret gaps on this
   *  preview page. `pim_nutrition_disabled` hard-blocks a real build (422) and
   *  flips `buildBlocked`; it also refuses the WRITER (materialization declines
   *  to persist blanks while the resolver is down) — the fix is configuration,
   *  not per-SKU healing. */
  warnings: z.array(runOutputWarningSchema),
  /** Canonical per-product inspection verdict for this candidate page.
   *  Parallel to `rows` (same page slice); a NOK product has no feed row but
   *  still appears here with its reasons. */
  verdictItems: z.array(runFeedPreviewVerdictItemSchema),
  /** Deterministic family plan for this page's final feed rows. */
  variantFamilies: runVariantFamilyPlanSchema,
  /** Variants on this page that are eligible to list but have no marketplace
   *  offer yet (the "new-only" offer build would emit a row for them). Null when
   *  the offer snapshot couldn't be read (e.g. the marketplace's platform can't
   *  read offers, or the live read failed) — the UI then hides the "needs offer"
   *  signals rather than implying every variant is covered. */
  offersNeededCount: z.number().int().nonnegative().nullable(),
  /** Variants on this page that already have a marketplace offer (excluded from
   *  the new-only build). Null under the same conditions as `offersNeededCount`. */
  offersAlreadyListedCount: z.number().int().nonnegative().nullable(),
  /** Storefront-role → feed-column-code map for the cards/grid view. */
  roles: runFeedPreviewRolesSchema,
  /** Magento (source) category leaf per config on this page, e.g. "Felpe", for
   *  the grid's secondary category caption. Page-scoped like `rows`; a config
   *  absent from the catalog mirror is simply omitted. */
  magentoCategoryByConfigSku: z.record(z.string(), z.string()),
  pagination: runFeedPreviewPaginationSchema.optional(),
})
export type RunFeedPreviewResponse = z.infer<typeof runFeedPreviewResponseSchema>

// GET /api/runs/:slug/skus/:configSku/preview
//
// Per-SKU verify view for the agent's debug loop: the layered
// "Magento facts -> writer-derived -> overrides -> rewrites -> final"
// shape, plus shipability (would this actually pass feed render),
// extraction state, and LLM warnings. Read-only. Composes existing
// primitives; never writes.
export const runSkuPreviewAttributeSourceSchema = z.enum([
  "static",
  "fact",
  "copy",
  "lookup",
  "transform",
  "template",
  "image_pick",
  "ai_generate",
  "nutrition_table",
  "manual",
  "ai_override",
  "human_override",
  "system_override",
])
export type RunSkuPreviewAttributeSource = z.infer<typeof runSkuPreviewAttributeSourceSchema>

export const runSkuPreviewAttributeSchema = z.object({
  attrCode: z.string(),
  scope: z.enum(["config", "simple"]),
  // canonical scope after normalisation
  simpleSku: z.string().nullable(),
  source: runSkuPreviewAttributeSourceSchema,
  // Output format + length limit declared on the writer rule. `format` drives
  // how the drawer renders the value (markdown gets a renderer); `maxChars`
  // turns the char count into a budget the reviewer can read against. Null when
  // the rule omits them or the attribute is a gap with no resolved rule.
  format: attributeFormatSchema.nullable(),
  maxChars: z.number().int().positive().nullable(),
  rawValue: z.string().nullable(),
  rewrittenValue: z.string().nullable(),
  rewritesApplied: z.array(z.string()),
  gap: z
    .object({
      reason: z.string(),
      message: z.string(),
    })
    .nullable(),
})
export type RunSkuPreviewAttribute = z.infer<typeof runSkuPreviewAttributeSchema>

export const runSkuPreviewShipabilityBlockerSchema = z.object({
  stage: z.enum(["tracking", "extract", "writer", "feed_build"]),
  reason: z.string(),
  attrCode: z.string().nullable(),
  simpleSku: z.string().nullable(),
  message: z.string(),
})
export type RunSkuPreviewShipabilityBlocker = z.infer<typeof runSkuPreviewShipabilityBlockerSchema>

export const runSkuPreviewResponseSchema = z.object({
  configSku: z.string(),
  marketplace: marketplaceIdSchema,
  category: z.string().nullable(),
  /** The active canonical writer identity — debugging provenance only. */
  writerIdentity: z.string().nullable(),
  // Copy field-role → attribute-code mapping, so the UI can identify which feed
  // columns carry the title / short description / long description without
  // hardcoding vendor-specific codes. Marketplace-neutral: the generic role keys
  // are resolved per marketplace from `copy.attributeCodes` in its config. Null
  // when the marketplace declares no copy block.
  copy: z
    .object({
      title: z.string(),
      shortDescription: z.string(),
      longDescription: z.string(),
    })
    .nullable(),
  // Feed attribute codes the product header already surfaces (brand + category),
  // so the dashboard can drop those rows from the raw attribute list and not show
  // them twice. Marketplace-neutral: resolved per marketplace from config. Null
  // when the marketplace doesn't declare one.
  brandAttrCode: z.string().nullable(),
  categoryAttrCode: z.string().nullable(),
  magento: z.object({
    breadcrumb: z.string().nullable(),
    manufacturer: z.string().nullable(),
    brand: z.string().nullable(),
    name: z.string().nullable(),
    description: z.string().nullable(),
    shortDescription: z.string().nullable(),
    images: z.array(z.object({ url: z.string() })),
    simples: z.array(
      z.object({
        sku: z.string(),
        ean: z.string().nullable(),
        price: z.number().nullable(),
        priceFinal: z.number().nullable(),
        stockStatus: z.string().nullable(),
        size: z.string().nullable(),
        color: z.string().nullable(),
        flavor: z.string().nullable(),
        isDelisted: z.boolean(),
        enabled: z.boolean(),
      })
    ),
  }),
  extract: z.object({
    state: z.enum([
      "extracted",
      "extracted_with_warnings",
      "validation_failed",
      "extract_error",
      "not_run",
    ]),
    modelUsed: z.string().nullable(),
    extractedAt: z.string().nullable(),
    validation: z
      .object({
        rejectedKeys: z.array(z.string()),
        message: z.string().optional(),
      })
      .nullable(),
    marketplaceCategory: z
      .object({
        id: z.string(),
        confidence: z.enum(["high", "medium", "low"]),
        rationale: z.string(),
      })
      .nullable(),
    warnings: z.array(z.string()),
  }),
  attributes: z.array(runSkuPreviewAttributeSchema),
  // Requirement level (required / recommended / …) for every attribute this
  // SKU's category owns, scoped to the marketplace's own locale. The drawer's
  // "required N/N" denominator and per-attribute chips read this directly, so
  // it doesn't make a second round-trip to the category-schema endpoint. Empty
  // when the SKU has no category. Keyed by attrCode.
  requirementByCode: z.record(z.string(), requirementLevelSchema),
  shipability: z.object({
    extractable: z.boolean(),
    feedBuildable: z.boolean(),
    blockers: z.array(runSkuPreviewShipabilityBlockerSchema),
  }),
})
export type RunSkuPreviewResponse = z.infer<typeof runSkuPreviewResponseSchema>
